import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useCallback } from 'react';
import { useRealtimeScope } from '@/shared/hooks/useRealtimeScope';
import { useSupabaseRealtime } from '@/shared/hooks/useSupabaseRealtime';
import type { Tables } from '@/shared/types/database';
import { rowToEvent } from '../services/supabaseCalendarService';
import { useCalendarStore } from '../stores/calendarStore';

type EventRow = Tables<'events'>;

/**
 * Realtime calendar events subscription (P7).
 *
 * Streams `public.events` changes for the current family into the calendar
 * store via the local-only `upsertRealtimeEvent` / `deleteRealtimeEvent`
 * setters (no service round-trips — an echo must never re-issue the write that
 * produced it). A no-op when realtime is disabled (see
 * {@link useSupabaseRealtime}).
 *
 * @module features/family-calendar/hooks/useRealtimeEvents
 */
export function useRealtimeEvents(): void {
  const { familyId } = useRealtimeScope();
  const upsertRealtimeEvent = useCalendarStore((s) => s.upsertRealtimeEvent);
  const deleteRealtimeEvent = useCalendarStore((s) => s.deleteRealtimeEvent);
  const refetch = useCalendarStore((s) => s.refetch);

  const handleChange = useCallback(
    (payload: RealtimePostgresChangesPayload<EventRow>) => {
      if (payload.eventType === 'DELETE') {
        const oldId = payload.old.id;
        if (oldId) {
          deleteRealtimeEvent(oldId);
        }
        return;
      }

      const row = payload.new;
      // Defense in depth: ignore rows outside the current family even if the
      // server-side filter is ever absent.
      if (familyId !== null && row.family_id !== familyId) {
        return;
      }
      upsertRealtimeEvent(rowToEvent(row));
    },
    [familyId, upsertRealtimeEvent, deleteRealtimeEvent]
  );

  useSupabaseRealtime<EventRow>({
    table: 'events',
    enabled: familyId !== null,
    ...(familyId !== null && { filter: `family_id=eq.${familyId}` }),
    onChange: handleChange,
    // Changes streamed while the channel was down are lost — refetch on
    // recovery to close the gap.
    onReconnect: () => void refetch(),
  });
}
